import moment from 'moment'
import ApiClient from 'api-client/ApiClient'
import { sleep } from 'api-client/helpers'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { listVolumeSnapshots } from 'app/plugins/openstack/components/storage/snapshots/actions'
import store from 'app/store'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import CustomAction from 'core/actions/CustomAction'
import ListAction from 'core/actions/ListAction'
import { cacheActions } from 'core/caching/cacheReducers'
import { NotificationType, notificationActions } from 'core/notifications/notificationReducers'
import { sessionActions } from 'core/session/sessionReducers'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import { listImages } from 'openstack/components/images/actions'
import { listPorts } from 'openstack/components/networks/actions'
import { listLeasePolicies, listLeases } from 'openstack/components/vms/leases/actions'
import {
  listAllServerGroups,
  listServerGroups,
} from 'openstack/components/vms/server-groups/actions'
import { getLastRefreshTime } from 'openstack/helpers'
import { isVolumeBackedVm } from './helpers'
import { mountISO as mountIsoForVm, unmountISO as unmountIsoForVm } from './isoOperations'
import { ServerStates, type VirtualMachine } from './newModel'
import type { VMFilterParams } from './useVMFilterConfigs'
import { convertVMFilterParamsToQueryParams } from './utils'

/**
 * Type alias for cacheable record format used in cache operations
 * Equivalent to casting: as unknown as Record<string, unknown>
 * Used with updateItem() where item must be Record<string, unknown>
 * Single source to replace if the pattern needs to change globally
 */
type CacheableRecord = Record<string, unknown>

/**
 * Type alias for cacheable record array format used in cache operations
 * Equivalent to casting: as unknown as Record<string, unknown>[]
 * Used with replaceAll/upsertAll() where items must be Record<string, unknown>[]
 * Single source to replace if the pattern needs to change globally
 */
type CacheableRecordArray = Record<string, unknown>[]

const { dispatch } = store
const { nova, neutron, cinder } = ApiClient.getInstance()

const getCurrentNovaFormattedTimestamp = () => {
  const timeStamp = moment().utc().format()

  return timeStamp
}

export const getNovaVersion = () => {
  return nova.getVersionDetails()
}

const updateVmCachesSingle = (vm) => {
  const state = store.getState()
  const currentUser = state.session?.username
  const userId = state.session?.userDetails?.userId
  const currentTenant = state.preferences?.[currentUser]?.root?.currentTenant
  const isTenantVm = vm?.tenant_id === currentTenant
  const isUserVm = vm?.user_id === userId

  if (isAdmin()) {
    dispatch(
      cacheActions.updateItem({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.AllOpenstackVirtualMachines,
        params: { id: vm?.id },
        item: vm,
      }),
    )
    dispatch(
      cacheActions.updateItem({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.SingleOpenstackVirtualMachine,
        params: { id: vm?.id },
        item: vm,
      }),
    )
    if (isTenantVm) {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id: vm?.id },
          item: vm,
        }),
      )
    }
  } else {
    dispatch(
      cacheActions.updateItem({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.OpenstackVirtualMachines,
        params: { id: vm?.id },
        item: vm,
      }),
    )
    if (isUserVm) {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: { id: vm?.id },
          item: vm,
        }),
      )
    }
  }
}

const updateVmCachesMulti = (vms) => {
  const state = store.getState()
  const currentUser = state.session?.username
  const userId = state.session?.userDetails?.userId
  const currentTenant = state.preferences?.[currentUser]?.root?.currentTenant
  const tenantVms = vms?.filter((vm) => vm?.tenant_id === currentTenant)
  const userVms = vms?.filter((vm) => vm?.user_id === userId)

  if (isAdmin()) {
    dispatch(
      cacheActions.upsertAll({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.AllOpenstackVirtualMachines,
        params: {},
        items: vms,
      }),
    )
    if (tenantVms?.length) {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: {},
          items: tenantVms,
        }),
      )
    }
  } else {
    dispatch(
      cacheActions.upsertAll({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.OpenstackVirtualMachines,
        params: {},
        items: vms,
      }),
    )
    if (userVms?.length) {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: {},
          items: userVms,
        }),
      )
    }
  }
}

// Lite APIs
export const virtualMachinesLite = ActionsSet.make<DataKeys.OpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVirtualMachinesLite,
  cacheKey: DataKeys.OpenstackVirtualMachinesLite,
})

export const listVirtualMachinesLite = virtualMachinesLite.add(
  new ListAction<DataKeys.OpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const searchParams = params?.host ? { host: params.host } : undefined
    return nova.getAllInstancesLite('', searchParams)
  }),
)

// All VMs - Lite
export const allVirtualMachinesLite = ActionsSet.make<DataKeys.AllOpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllOpenstackVirtualMachinesLite,
  cacheKey: DataKeys.AllOpenstackVirtualMachinesLite,
})

export const listAllVirtualMachinesLite = allVirtualMachinesLite.add(
  new ListAction<DataKeys.AllOpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const searchParams = params?.host ? { host: params.host } : undefined
    return nova.getAllInstancesLite('all_tenants=true', searchParams)
  }),
)

// Self VMs - Lite (for member users)
export const selfVirtualMachinesLite = ActionsSet.make<DataKeys.SelfOpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SelfOpenstackVirtualMachinesLite,
  cacheKey: DataKeys.SelfOpenstackVirtualMachinesLite,
})

export const listSelfVirtualMachinesLite = selfVirtualMachinesLite.add(
  new ListAction<DataKeys.SelfOpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const state = store.getState()
    const userId = state?.session?.userDetails?.id
    const searchParams = params?.host ? { host: params.host } : undefined
    // Get only VMs for the current user
    return nova.getAllInstancesLite(userId ? `owner=${userId}` : '', searchParams)
  }),
)

// Active VMs
export const statewiseVirtualMachinesLite =
  ActionsSet.make<DataKeys.OpenstackStatewiseVirtualMachinesLite>({
    uniqueIdentifier: 'state',
    entityName: entityNamesByKey.OpenstackStatewiseVirtualMachinesLite,
    cacheKey: DataKeys.OpenstackStatewiseVirtualMachinesLite,
  })

export const listStatewiseVirtualMachinesLite = statewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const query_params = params?.host ? `host=${params.host}&` : ''

      const { ACTIVE, BUILDING, PAUSED, STOPPED, SUSPENDED, TOTAL_COUNT } =
        await nova.getVirtualMachinesCount(query_params)

      return [
        {
          [ServerStates.ACTIVE]: ACTIVE,
          [ServerStates.BUILDING]: BUILDING,
          [ServerStates.PAUSED]: PAUSED,
          [ServerStates.STOPPED]: STOPPED,
          [ServerStates.SUSPENDED]: SUSPENDED,
          [ServerStates.TOTAL]: TOTAL_COUNT,
        },
      ]
    },
  ),
)

// All Active VMs
export const allStatewiseVirtualMachinesLite =
  ActionsSet.make<DataKeys.AllOpenstackStatewiseVirtualMachinesLite>({
    uniqueIdentifier: 'state',
    entityName: entityNamesByKey.AllOpenstackStatewiseVirtualMachinesLite,
    cacheKey: DataKeys.AllOpenstackStatewiseVirtualMachinesLite,
  })

export const listAllStatewiseVirtualMachinesLite = allStatewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.AllOpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const query_params = params?.host
        ? `all_tenants=true&host=${params.host}`
        : 'all_tenants=true'

      const { ACTIVE, BUILDING, PAUSED, STOPPED, SUSPENDED, TOTAL_COUNT } =
        await nova.getVirtualMachinesCount(query_params)

      return [
        {
          [ServerStates.ACTIVE]: ACTIVE,
          [ServerStates.BUILDING]: BUILDING,
          [ServerStates.PAUSED]: PAUSED,
          [ServerStates.STOPPED]: STOPPED,
          [ServerStates.SUSPENDED]: SUSPENDED,
          [ServerStates.TOTAL]: TOTAL_COUNT,
        },
      ]
    },
  ),
)

// Self Statewise VMs (for member users)
export const selfStatewiseVirtualMachinesLite =
  ActionsSet.make<DataKeys.SelfOpenstackStatewiseVirtualMachinesLite>({
    uniqueIdentifier: 'state',
    entityName: entityNamesByKey.SelfOpenstackStatewiseVirtualMachinesLite,
    cacheKey: DataKeys.SelfOpenstackStatewiseVirtualMachinesLite,
  })

export const listSelfStatewiseVirtualMachinesLite = selfStatewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.SelfOpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id

      let query_params = userId ? `owner=${userId}&` : ''
      if (params?.host) {
        query_params += `host=${params.host}&`
      }

      const { ACTIVE, BUILDING, PAUSED, STOPPED, SUSPENDED, TOTAL_COUNT } =
        await nova.getVirtualMachinesCount(query_params)

      return [
        {
          [ServerStates.ACTIVE]: ACTIVE,
          [ServerStates.BUILDING]: BUILDING,
          [ServerStates.PAUSED]: PAUSED,
          [ServerStates.STOPPED]: STOPPED,
          [ServerStates.SUSPENDED]: SUSPENDED,
          [ServerStates.TOTAL]: TOTAL_COUNT,
        },
      ]
    },
  ),
)

export const allVirtualMachineActions = ActionsSet.make<DataKeys.AllOpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllOpenstackVirtualMachines,
  cacheKey: DataKeys.AllOpenstackVirtualMachines,
})

export const listAllVirtualMachines = allVirtualMachineActions.add(
  new ListAction<DataKeys.AllOpenstackVirtualMachines>(async (params) => {
    // Is an admin-only API
    if (!isAdmin()) {
      return []
    }

    const {
      limit = 50,
      marker,
      searchParams,
      sortKey = 'created_at',
      sortDir = 'desc',
    } = params || {}

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      allTenants: true,
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const tenant_id = (searchParams as unknown as VMFilterParams)?.tenant_id
    const { TOTAL_COUNT: totalCount } = await nova.getVirtualMachinesCount(
      tenant_id ? '' : 'all_tenants=true',
      tenant_id,
    )

    // Calculate current position
    const state = store.getState()
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.AllOpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        allVmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  }).addDependency(DataKeys.AllServerGroups),
  // .addDependency(DataKeys.ManagementUsers)
  // .addDependency(DataKeys.Images)
  // .addDependency(DataKeys.OpenstackVolumesLite)
  // .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listAllVirtualMachinesNextPage = allVirtualMachineActions.add(
  new CustomAction<DataKeys.AllOpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      if (!isAdmin()) {
        return []
      }

      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        allTenants: true,
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const tenantId = (searchParams as unknown as VMFilterParams)?.tenant_id
        const { TOTAL_COUNT } = await nova.getVirtualMachinesCount(
          tenantId ? '' : 'all_tenants=true',
          tenantId,
        )
        totalCount = TOTAL_COUNT
      } else {
        // Get totalCount from session for subsequent pages
        const state = store.getState()
        totalCount = state.session?.allVmsPagination?.totalCount
      }

      // Calculate current position
      const state = store.getState()
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.AllOpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          allVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

// Lightweight search action for all VMs - no dependencies
export const searchAllVirtualMachines = allVirtualMachineActions.add(
  new CustomAction<DataKeys.AllOpenstackVirtualMachines>(
    'searchAllVirtualMachines',
    async (params) => {
      if (!isAdmin()) {
        return []
      }

      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          allTenants: true,
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      if (!marker) {
        const tenant_id = (searchParams as unknown as VMFilterParams)?.tenant_id
        let query_params = tenant_id ? '' : `all_tenants=true`
        if (searchParams) {
          query_params += convertVMFilterParamsToQueryParams(searchParams)
        }
        promises.push(nova.getVirtualMachinesCount(query_params, tenant_id))
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.TOTAL_COUNT || 0
      } else {
        // Get totalCount from session for subsequent search pages
        const state = store.getState()
        totalCount = state.session?.allVmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          allVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
    },
  ),
)

export const virtualMachineActions = ActionsSet.make<DataKeys.OpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVirtualMachines,
  cacheKey: DataKeys.OpenstackVirtualMachines,
  addCacheKey: DataKeys.AllOpenstackVirtualMachines,
})

export const listVirtualMachines = virtualMachineActions.add(
  new ListAction<DataKeys.OpenstackVirtualMachines>(async (params) => {
    const {
      limit = 50,
      marker,
      searchParams,
      sortKey = 'created_at',
      sortDir = 'desc',
    } = params || {}

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const { TOTAL_COUNT: totalCount } = await nova.getVirtualMachinesCount()

    // Calculate current position
    const state = store.getState()
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.OpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        vmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  })
    // .addDependency(DataKeys.ManagementUsers)
    // .addDependency(DataKeys.Images)
    // .addDependency(DataKeys.ClusterHostGroups)
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    // .addDependency(DataKeys.OpenstackVolumesLite)
    .addDependency(DataKeys.Leases),
  // .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listVirtualMachinesNextPage = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const { TOTAL_COUNT } = await nova.getVirtualMachinesCount()
        totalCount = TOTAL_COUNT
      } else {
        // Get totalCount from session for subsequent pages
        const state = store.getState()
        totalCount = state.session?.vmsPagination?.totalCount
      }

      // Calculate current position
      const state = store.getState()
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.OpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          vmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const createVirtualMachine = virtualMachineActions.add(
  new CreateAction<DataKeys.OpenstackVirtualMachines, { body }>(
    async ({ body }): Promise<VirtualMachine> => {
      const created = await nova.createInstance(body)
      const adminPass = created?.adminPass
      const instanceId = created?.id
      // Nova DB needs some time to write, CQRS reads too fast
      await sleep(1000)
      const newVm = await nova.getInstance(instanceId)

      // Custom update for self VMs
      // Todo: Perhaps maybe addCacheKey a list?
      if (!isAdmin() && newVm?.id) {
        dispatch(
          cacheActions.updateItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            params: { id: newVm?.id },
            item: newVm as unknown as Record<string, unknown>,
          }),
        )
      }

      return {
        ...(newVm || {}),
        id: instanceId,
        adminPass,
      } as VirtualMachine
    },
  ),
)

export const createVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'createMultipleVms',
    // @ts-ignore
    async ({ body }) => {
      const response = await nova.createInstances(body)
      // Nova DB needs some time to write, CQRS reads too fast
      await sleep(1000)
      const reservationId = response.reservation_id
      const vms = await nova.getInstancesForReservationId(reservationId)
      return vms
    },
    (result, { id }) => {
      // Do not need to update cache anymore manually, we
      // just reload the pagination instead
      // updateVmCachesMulti(result)
    },
  ),
)

export const updateVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body: any }>(
    'updateVM',
    async ({ id, body }) => {
      const updatedVm = await nova.updateInstance(id, body)
      return updatedVm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

// FR-001: Fetch ports before any mutation
// FR-002: Rename VM (Nova) then update port DNS names (Neutron)
// FR-003: Rollback all succeeded operations on any failure
// FR-005: Skip port updates if Nova rename fails
// FR-006: Handle VMs with zero ports
export const renameVirtualMachine = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; name: string; originalName: string }
  >(
    'renameVm',
    async ({ id, name, originalName }) => {
      // FR-001: Fetch all ports for VM before any mutations
      const ports = await neutron.getPortsForVm({ vmId: id })
      const originalPortDnsNames = (ports || []).map((p) => ({
        id: p.id,
        dns_name: p.dns_name,
      }))

      // FR-002: Rename VM first (Nova)
      // FR-005: If this fails, no port updates attempted — error propagates
      const updatedVm = await nova.updateInstance(id, { server: { name } })

      // FR-006: Skip port updates if no ports attached
      if (!ports || ports.length === 0) {
        return updatedVm
      }

      // Sanitize name for DNS: only alphanumeric and hyphens allowed (RFC 952)
      const sanitized = name.replace(/[^a-zA-Z0-9-]/g, '').replace(/^-+|-+$/g, '')
      const dnsName = sanitized || `server-${id}`

      // FR-002: Update dns_name on all ports (Neutron)
      const portResults = await Promise.allSettled(
        ports.map((port) =>
          neutron.updatePort({
            id: port.id,
            body: { port: { dns_name: dnsName } },
          }),
        ),
      )

      const failedResults = portResults.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      )

      if (failedResults.length > 0) {
        // FR-003: Rollback succeeded port DNS names
        const succeededPorts = portResults
          .map((r, i) => (r.status === 'fulfilled' ? originalPortDnsNames[i] : null))
          .filter(Boolean)

        await Promise.allSettled(
          succeededPorts.map((p) =>
            neutron.updatePort({
              id: p.id,
              body: { port: { dns_name: p.dns_name } },
            }),
          ),
        )

        // FR-003: Rollback VM name
        if (originalName !== undefined) {
          await nova.updateInstance(id, { server: { name: originalName } }).catch(() => {})
        }

        throw failedResults[0].reason
      }

      return updatedVm
    },
    // FR-004: Update VM caches on success
    (result) => {
      updateVmCachesSingle(result)
    },
  ),
)

// Lightweight search action - no dependencies, for faster search
export const searchVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'searchVirtualMachines',
    async (params) => {
      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      // Fetch total count from lite API only on first page (no marker)
      if (!marker) {
        let query_params = ''
        if (searchParams) {
          query_params += convertVMFilterParamsToQueryParams(searchParams)
        }
        promises.push(nova.getVirtualMachinesCount(query_params))
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached tot alCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.TOTAL_COUNT || 0
      } else {
        // Get totalCount from session for subsequent search pages
        const state = store.getState()
        totalCount = state.session?.vmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          vmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
    },
  ),
)

export const deleteVirtualMachine = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; preCreatedPorts: string[]; volumesToDelete?: string[] }
  >(
    'deleteVm',
    async ({ id, preCreatedPorts, volumesToDelete }) => {
      try {
        if (volumesToDelete?.length) {
          const results = await Promise.allSettled(
            volumesToDelete.map((volumeId) =>
              nova.updateVolumeAttachment({
                id,
                volumeId,
                body: { volumeAttachment: { volumeId, delete_on_termination: true } },
              }),
            ),
          )
          const failedCount = results.filter((r) => r.status === 'rejected').length
          if (failedCount) {
            const message = `Failed to mark ${failedCount} volume(s) for deletion. These volumes will not be deleted on VM termination.`
            dispatch(
              notificationActions.registerNotification({
                title: 'Volume Deletion Warning',
                message,
                data: { message },
                type: NotificationType.warning,
              }),
            )
          }
        }
        await nova.deleteInstance(id)
        // Putting this getInstance block in try catch so that it doesnt propogate error to the components
        // This is to handle the case where the VM is already deleted and getInstance throws an 401 error
        // Delete pre-created ports (simple network or managed with subnet selection) when the VM is removed
        if (preCreatedPorts?.length) {
          const promises = preCreatedPorts.map((port) => neutron.deletePort(port))
          await Promise.all(promises)
        }

        // Once the VM is deleted we want to remove it from the list
        return null
      } catch (error) {
        throw error
      }
    },
    (_, { id }) => {
      dispatch(
        cacheActions.removeItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
        }),
      )
      dispatch(
        cacheActions.removeItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
        }),
      )
      dispatch(
        cacheActions.removeItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: { id },
        }),
      )
      dispatch(
        cacheActions.removeItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          params: { id },
        }),
      )
      return
    },
  ),
)

export const refreshVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string }>(
    'vmRefresh',
    async ({ id }) => {
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      updateVmCachesSingle(result)
    },
  ),
)

export const powerVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'vmOperation',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const addSecurityGroups = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; securityGroupNames?: string[] }
  >(
    'addSecurityGroups',
    async ({ id, securityGroupNames }) => {
      for (const securityGroupName of securityGroupNames) {
        await nova.addSecurityGroupAction({ id, securityGroupName })
      }

      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const removeSecurityGroups = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; securityGroupNames?: string[] }
  >(
    'removeSecurityGroups',
    async ({ id, securityGroupNames }) => {
      for (const securityGroupName of securityGroupNames) {
        await nova.removeSecurityGroupAction({ id, securityGroupName })
      }

      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle({ ...result, security_groups: result['security_groups'] || [] })
    },
  ),
)

export const snapshotVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'snapshotVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const isVolumeBackedInstance = isVolumeBackedVm(vm)

      isVolumeBackedInstance ? listVolumeSnapshots.call({}) : listImages.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const getConsoleEndpoint = async ({ id }) => {
  const response = await nova.createConsole({ vmId: id })
  return response?.remote_console?.url
}

export const migrateVirtualMachine = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; body?: any; apiVersion?: string }
  >(
    'migrateVm',
    async ({ id, body, apiVersion }) => {
      await nova.instanceActionWithSpecificVersion({ id, body, version: apiVersion })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
    {
      errorMessage: (err) => {
        const status =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined
        if (status === 406)
          return 'Migration failed: this deployment does not support cross-cluster migration (requires Nova microversion 2.101).'
        if (status === 400)
          return 'Migration failed: the migration request was rejected. Verify the VM state and the target PCD cluster.'
        return 'Failed to migrate virtual machine. Please try again.'
      },
    },
  ),
)

export const addFixedIpToVm = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'addFixedIp',
    async ({ id, body }) => {
      await nova.createInterface({ id, body })
      const vm = await nova.getInstance(id)
      listPorts.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const detachFixedIpFromVm = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; portId: string }>(
    'detachFixedIp',
    async ({ id, portId }) => {
      await nova.detachInterface({ id, portId })
      const vm = await nova.getInstance(id)
      listPorts.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const updateVmMetadata = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'updateVmMetadata',
    async ({ id, body }) => {
      await nova.setInstanceMetadata({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const rebuildVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'rebuildVm',
    async ({ id, body }) => {
      await nova.instanceActionWithSpecificVersion({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const resizeVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'resizeVm',
    async ({ id, body }) => {
      // Remove the migration from the cache
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const confirmResizeVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'confirmResizeVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const rescueVmAction = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body: any }>(
    'rescueVm',
    async ({ id, body }) => {
      const response = await nova.rescueVmAction({ id, body })
      const vm = await nova.getInstance(id)
      return { ...vm, adminPass: response?.adminPass }
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const unrescueVmAction = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'unrescueVm',
    async ({ id, body }) => {
      await nova.unrescueVmAction({ id })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const hotplugVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'hotplugVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const pollVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'pollVirtualMachines',
    async (params) => {
      const state = store.getState()
      const lastTimestamp = state.session.vmsLastPolledTimestamp
      if (!lastTimestamp) {
        return []
      }
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        // Poll current page with filters - refresh all VMs on current page
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
        })
        return vms
      } else {
        const vms = await nova.getInstancesSince(lastTimestamp)
        return vms
      }
    },
    (result: VirtualMachine[]) => {
      updateVmCachesMulti(result)
    },
  ),
)

export const pollAllVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'pollAllVirtualMachines',
    async (params) => {
      const state = store.getState()
      const lastTimestamp = state.session.vmsLastPolledTimestamp
      if (!lastTimestamp) {
        return []
      }
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        // Poll current page with filters - refresh all VMs on current page
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
          allTenants: true,
        })
        return vms
      } else {
        // Fallback: poll all VMs that changed since last poll
        const vms = await nova.getAllInstancesSince(lastTimestamp)
        return vms
      }
    },
    (result: VirtualMachine[]) => {
      updateVmCachesMulti(result)
    },
  ),
)

export const selfVirtualMachineActions = ActionsSet.make<DataKeys.SelfOpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SelfOpenstackVirtualMachines,
  cacheKey: DataKeys.SelfOpenstackVirtualMachines,
  addCacheKey: DataKeys.OpenstackVirtualMachines,
})

export const listSelfVirtualMachines = selfVirtualMachineActions.add(
  new ListAction<DataKeys.SelfOpenstackVirtualMachines>(async (params) => {
    const state = store.getState()
    const userId = state?.session?.userDetails?.id
    const {
      limit = 50,
      marker,
      searchParams,
      sortKey = 'created_at',
      sortDir = 'desc',
    } = params || {}

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      userId,
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const { TOTAL_COUNT: totalCount } = await nova.getVirtualMachinesCount(`owner=${userId}`)

    // Calculate current position
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.SelfOpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        selfVmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  })
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    .addDependency(DataKeys.Leases),
  // .addDependency(DataKeys.ManagementUsers)
  // .addDependency(DataKeys.Images)
  // .addDependency(DataKeys.ClusterHostGroups)
  // .addDependency(DataKeys.OpenstackVolumesLite)
  // .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listSelfVirtualMachinesNextPage = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        userId,
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const { TOTAL_COUNT } = await nova.getVirtualMachinesCount(`owner=${userId}`)
        totalCount = TOTAL_COUNT
      } else {
        // Get totalCount from session for subsequent pages
        totalCount = state.session?.selfVmsPagination?.totalCount
      }

      // Calculate current position
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.SelfOpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          selfVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const pollSelfVirtualMachines = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'pollSelfVirtualMachines',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const lastTimestamp = state?.session?.vmsLastPolledTimestamp

      if (!lastTimestamp) {
        return []
      }
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          userId,
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
        })
        return vms
      } else {
        const vms = await nova.getSelfInstancesSince(lastTimestamp, userId)
        return vms
      }
    },
    (result: VirtualMachine[]) => {
      updateVmCachesMulti(result)
    },
  ),
)

// Lightweight search action for self VMs - no dependencies
export const searchSelfVirtualMachines = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'searchSelfVirtualMachines',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const {
        limit = 50,
        marker,
        searchParams,
        sortKey = 'created_at',
        sortDir = 'desc',
      } = params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          userId,
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      if (!marker) {
        const query_params = `owner=${userId}${convertVMFilterParamsToQueryParams(searchParams)}`
        promises.push(nova.getVirtualMachinesCount(query_params))
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.TOTAL_COUNT || 0
      } else {
        // Get totalCount from session for subsequent search pages
        totalCount = state.session?.selfVmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: getCurrentNovaFormattedTimestamp(),
          selfVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: VirtualMachine[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result as unknown as CacheableRecordArray,
          }),
        )
      }
    },
  ),
)

export const singleVmActions = ActionsSet.make<DataKeys.SingleOpenstackVirtualMachine>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SingleOpenstackVirtualMachine,
  cacheKey: DataKeys.SingleOpenstackVirtualMachine,
})

export const listSingleVirtualMachine = singleVmActions.add(
  new ListAction<DataKeys.SingleOpenstackVirtualMachine, { id: string }>(async ({ id }) => {
    const vm = await nova.getInstance(id)
    return vm ? [vm] : []
  })
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    .addDependency(DataKeys.Leases)
    .addDependency(DataKeys.VmMigrations),
  // .addDependency(DataKeys.OpenstackVolumesLite)
  // .addDependency(DataKeys.FederatedDomainUsers)
  // .addDependency(DataKeys.ManagementUsers)
  // .addDependency(DataKeys.Images)
  // .addDependency(DataKeys.ClusterHostGroups)
)

export const updateSingleVm = singleVmActions.add(
  new CustomAction<DataKeys.SingleOpenstackVirtualMachine, { id: string }>(
    'updateSingleVm',
    async ({ id }) => {
      const vm = await nova.getInstance(id)
      return [vm]
    },
    (result: VirtualMachine[], { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          items: result,
        }),
      )
    },
  ),
)

export const toggleDeleteOnTermination = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; volumeId: string; body?: any }>(
    'toggleDeleteOnTermination',
    async ({ id, volumeId, body }) => {
      await nova.updateVolumeAttachment({ id, volumeId, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
    },
  ),
)

export const addServerGroupVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'addServerGroupVirtualMachine',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const serverGroups = await nova.getServerGroups()
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.ServerGroups,
          items: serverGroups,
        }),
      )
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
    },
  ),
)

export const removeServerGroupVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'removeServerGroupVirtualMachine',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const serverGroups = await nova.getServerGroups()
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.ServerGroups,
          items: serverGroups,
        }),
      )
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result as unknown as Record<string, unknown>,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result as unknown as Record<string, unknown>,
        }),
      )
    },
  ),
)

export const updateVirtualMachineOwner = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'updateVirtualMachineOwner',
    async ({ id, body }) => {
      await nova.updateVirtualMachineOwner({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
    },
  ),
)

export interface ChangeTenantBody {
  changeTenant: {
    project_id: string
  }
}

export const changeTenantVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body: ChangeTenantBody }>(
    'changeTenantVirtualMachine',
    async ({ id, body }) => {
      await nova.changeTenantForVirtualMachine({ id, body })
    },
  ),
)

export const updateVmVolumeTermination = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; enableVolumeTerm: any[]; disableVolumeTerm: any[] }
  >(
    'updateVmVolumeTermination',
    async ({ id, enableVolumeTerm, disableVolumeTerm }) => {
      const enablePromises = enableVolumeTerm.map((volumeId) =>
        nova.updateVolumeAttachment({
          id,
          volumeId,
          body: { volumeAttachment: { volumeId, delete_on_termination: true } },
        }),
      )
      const disablePromises = disableVolumeTerm.map((volumeId) =>
        nova.updateVolumeAttachment({
          id,
          volumeId,
          body: { volumeAttachment: { volumeId, delete_on_termination: false } },
        }),
      )
      await Promise.allSettled([...enablePromises, ...disablePromises])
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          params: { id },
          item: result as unknown as CacheableRecord,
        }),
      )
    },
  ),
)

export const refreshVmDependencies = (allTenants: boolean = false) => {
  listLeasePolicies.call({})
  listLeases.call({})
  // listSimpleVolumes.call({})
  // listFederatedDomainUsers.call({})
  // listUsers.call({})
  // listImages.call({})
  // listClusterHostGroups.call({})
  if (allTenants) {
    listAllServerGroups.call({})
  } else {
    listServerGroups.call({})
  }
}

export const anyVmDependencyNotLoaded = (allTenants: boolean = false) => {
  return (
    !getLastRefreshTime(DataKeys.LeasePolicies) ||
    !getLastRefreshTime(DataKeys.Leases) ||
    // !getLastRefreshTime(DataKeys.ManagementUsers) ||
    // !getLastRefreshTime(DataKeys.Images) ||
    // !getLastRefreshTime(DataKeys.ClusterHostGroups) ||
    // !getLastRefreshTime(DataKeys.OpenstackVolumesLite) ||
    // !getLastRefreshTime(DataKeys.FederatedDomainUsers) ||
    (allTenants
      ? !getLastRefreshTime(DataKeys.AllServerGroups)
      : !getLastRefreshTime(DataKeys.ServerGroups))
  )
}

export const getVirtualMachine = ({ id }) => {
  return nova.getInstance(id)
}

export const simpleMigrateVirtualMachine = ({ id, body }) => {
  return nova.instanceActionWithSpecificVersion({ id, body })
}

export const getVmEvents = ({ id }) => {
  return nova.getVmEvents({ id })
}

export const getVmEventDetails = ({ vmId, requestId }) => {
  return nova.getVmEventDetails({ vmId, requestId })
}

export const searchVmAcrossTenants = ({ query, property }) => {
  return nova.searchInstanceAcrossTenants({ query, property })
}

export const simpleAddVmTag = ({ id, tag }) => {
  return nova.addInstanceTag({ id, tag })
}

export const mountISOAction = ({
  vmId,
  isoImageId,
  vmName,
  isoImageSizeBytes,
  volumeType = undefined,
}) => {
  // FR MID-FR-003: forward the selected volume type so the ISO backing volume is
  // created with the tenant default (or user choice) instead of __DEFAULT__.
  return mountIsoForVm({
    vmId,
    isoImageId,
    vmName,
    isoImageSizeBytes,
    volumeType,
  })
}

export const unmountISOAction = ({ vmId, volumeId }) => {
  return unmountIsoForVm({ vmId, volumeId })
}

export const enableTerminationForVolumes = async (volumes) => {
  const results = await Promise.allSettled(
    volumes.map(({ vmId, volumeId }) =>
      nova.updateVolumeAttachment({
        id: vmId,
        volumeId,
        body: { volumeAttachment: { volumeId, delete_on_termination: true } },
      }),
    ),
  )
  const succeeded: string[] = []
  const failed: string[] = []
  results.forEach((result, i) => {
    const { volumeId } = volumes[i]
    if (result.status === 'fulfilled') {
      succeeded.push(volumeId)
    } else {
      failed.push(volumeId)
    }
  })
  return { succeeded, failed }
}
